import React, { useState, useEffect } from 'react'
import { DateRangePicker } from 'transfix-ui/components/DateRangePicker'
import { FilterMultiSelect } from 'transfix-ui/components/FilterMultiSelect'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import Select from 'transfix-ui/components/Select'
import SuggestLocation from 'transfix-ui/components/SuggestLocation/SuggestLocation'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { ISortOrderEnum } from 'types/graphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { TQuoteFilters } from 'apps/marketplace/pages/spot-quote-tool/historical-quotes/types'
import { UserIcon, LaneArrowIcon } from 'transfix-icons'
import { TextField } from 'transfix-ui'
import { defaultQuoteFilters } from '../../constants/quoteFilters'
import { IQuoteSortingOption } from '../../hooks/useQuoteSortingOptions'
import { AppliedQuoteFilters } from './AppliedQuoteFilters'
import { ShipperFilterButton } from './ShipperFilterButton'
import { SortHistoricalQuotesFilter } from './SortHistoricalQuotesFilter'
import { TestQuoteFilter } from './TestQuoteFilter'
import { useCreatedBy } from './hooks/useCreatedBy'
import { useDropType } from './hooks/useDropType'
import { useEquipment } from './hooks/useEquipment'
import { useExternalId } from './hooks/useExternalId'
import { useLocation } from './hooks/useLocation'
import { useMultiStop } from './hooks/useMultiStop'
import { usePriceRequestId } from './hooks/usePriceRequestId'
import { usePricedDate } from './hooks/usePricedDate'
import { useShipper } from './hooks/useShipper'

export interface IHistoricalQuotesFiltersProps {
  quotesFilters: TQuoteFilters
  numberOfPriceRequests: number
  dateLimit: string
  displayTotalCount: string
  setQuotesFilters: React.Dispatch<React.SetStateAction<TQuoteFilters>>
  setFocusedQuoteId: React.Dispatch<React.SetStateAction<string | null>>
  sortBy: string
  sortDirection: ISortOrderEnum
  defaultOptionLabel: string
  sortingOptions: IQuoteSortingOption[]
  onSortingOptionSelect: (id: string) => void
}

const useStyles = makeStyles(theme => ({
  sortBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  inputField: {
    marginRight: theme.spacing(2),
    width: 'auto',
  },
  locationParent: {
    display: 'flex',
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    marginBottom: theme.spacing(1.5),
  },
  secondRow: {
    marginBottom: theme.spacing(1.5),
  },
  pricedDate: {
    position: 'relative',
    top: '6px',
  },
  buttonRow: {
    marginRight: theme.spacing(1),
  },
  limitWidth: {
    width: '250px',
    height: theme.spacing(4),
  },
  locationRange: {
    width: '104px',
    marginRight: theme.spacing(2),
  },
  locationSelect: {
    marginRight: theme.spacing(0.5),
    width: theme.spacing(30),
    display: 'inline-block',
  },
  locationArrow: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(-1),
  },
}))

export function HistoricalQuotesFilters({
  dateLimit,
  defaultOptionLabel,
  displayTotalCount,
  numberOfPriceRequests,
  onSortingOptionSelect,
  quotesFilters,
  setFocusedQuoteId,
  setQuotesFilters,
  sortBy,
  sortDirection,
  sortingOptions,
}: IHistoricalQuotesFiltersProps) {
  const objectiveFourEnabled = useFeatureFlag('sqtObjectiveFour')
  const [displayLocation, setDisplayLocation] = useState<boolean>(false)

  // prevent buggy loading of SuggestLocation component that would happen on mount
  useEffect(() => {
    setDisplayLocation(true)
  }, [])

  // if the user has a focused quote and changes the filters, we need to unfocus the quote
  useEffect(() => {
    setFocusedQuoteId(null)
  }, [quotesFilters])

  const classes = useStyles()
  const { currentSelection, dateRangeOptions, setPricedDate } = usePricedDate({
    quotesFilters,
    setQuotesFilters,
  })
  const {
    clearExternalIdHandler,
    externalId,
    submitExternalIdHandler,
    typeExternalIdHandler,
  } = useExternalId({
    quotesFilters,
    setQuotesFilters,
  })

  const { dropTypeHandler, dropTypeOptions } = useDropType({ quotesFilters, setQuotesFilters })

  const {
    clearAllLocationFiltersHandler,
    deliveryLocation,
    deliveryRange,
    pickupLocation,
    pickupRange,
    rangeOptions,
    selectDeliveryLocationHandler,
    selectDeliveryRangeHandler,
    selectPickupLocationHandler,
    selectPickupRangeHandler,
  } = useLocation({
    quotesFilters,
    setQuotesFilters,
  })

  const { multiStopHandler, multiStopOptions } = useMultiStop({ quotesFilters, setQuotesFilters })

  const {
    clearPriceRequestIdHandler,
    priceRequestId,
    submitPriceRequestIdHandler,
    typePriceRequestIdHandler,
  } = usePriceRequestId({
    quotesFilters,
    setQuotesFilters,
  })

  const { clearShipperHandler, selectShipperHandler, shipper } = useShipper({
    quotesFilters,
    setQuotesFilters,
  })
  const { equipmentHandler, equipmentOptions } = useEquipment({ quotesFilters, setQuotesFilters })
  const { createdByHandler, createdByOptions } = useCreatedBy({ quotesFilters, setQuotesFilters })

  const enterHandler = (eventKey: string, action: () => void) => {
    if (eventKey === 'Enter') {
      action()
    }
  }

  const enterPriceRequestIdHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    enterHandler(event.key, submitPriceRequestIdHandler)
  }

  const enterExternalIdHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    enterHandler(event.key, submitExternalIdHandler)
  }

  const clearAllHandler = () => {
    clearAllLocationFiltersHandler()
    clearShipperHandler()
    clearPriceRequestIdHandler()
    clearExternalIdHandler()
    setPricedDate(null)
    setQuotesFilters(defaultQuoteFilters)
  }

  const showAutobidderMessage =
    quotesFilters.createdBy?.includes('autobidder') && !!quotesFilters.shipperApplicationId
  const autobidderMessage = (
    <>
      <strong>Note:</strong> There may be additional price requests displayed here. i.e. This
      shipper might have more than 1 autobidder
    </>
  )

  return (
    <div>
      <div>
        <div className={classes.filterRow}>
          {displayLocation && (
            <div className={classes.locationParent}>
              <div className={classes.locationSelect}>
                <SuggestLocation
                  placeholder='PU City, State, Zip'
                  resultTypes={['City', 'State', 'PostalCode']}
                  onChange={selectPickupLocationHandler}
                  value={pickupLocation}
                />
              </div>
              <div className={classes.locationRange}>
                <Select
                  onChange={selectPickupRangeHandler}
                  options={rangeOptions}
                  value={pickupRange}
                />
              </div>
              <div className={classes.locationArrow}>
                <LaneArrowIcon size='xlarge' />
              </div>
              <div className={classes.locationSelect}>
                <SuggestLocation
                  placeholder='DEL City, State, Zip'
                  resultTypes={['City', 'State', 'PostalCode']}
                  onChange={selectDeliveryLocationHandler}
                  value={deliveryLocation}
                />
              </div>
              <div className={classes.locationRange}>
                <Select
                  onChange={selectDeliveryRangeHandler}
                  options={rangeOptions}
                  value={deliveryRange}
                />
              </div>
            </div>
          )}
          <div className={classes.limitWidth}>
            <TextField
              className={classes.inputField}
              onBlur={submitPriceRequestIdHandler}
              onChange={typePriceRequestIdHandler}
              onClearCallback={clearPriceRequestIdHandler}
              onKeyPress={enterPriceRequestIdHandler}
              value={priceRequestId}
              icon='search'
              placeholder='Price Request ID'
              dataTest='price-request-id-text-field'
              clearable
            />
          </div>
          <div className={classes.limitWidth}>
            <TextField
              className={classes.inputField}
              onBlur={submitExternalIdHandler}
              onChange={typeExternalIdHandler}
              onClearCallback={clearExternalIdHandler}
              value={externalId}
              onKeyPress={enterExternalIdHandler}
              icon='search'
              placeholder='Shipment ID (External), PO, BOL'
              dataTest='external-id-text-field'
              clearable
            />
          </div>
        </div>
        <div className={classes.secondRow}>
          <FilterMultiSelect
            className={classes.buttonRow}
            label='Created By'
            dataTest='created-by-multiselect'
            id='createdBy'
            options={createdByOptions}
            icon={<UserIcon size='large' />}
            onSelect={createdByHandler}
            optionsLabel='Created By'
          />
          {objectiveFourEnabled && (
            <FilterMultiSelect
              className={classes.buttonRow}
              label='Stops'
              dataTest='multistop-multiselect'
              id='isMultiStop'
              options={multiStopOptions}
              onSelect={multiStopHandler}
              optionsLabel='Stops'
            />
          )}
          <span className={classes.buttonRow}>
            <ShipperFilterButton
              clearShipperHandler={clearShipperHandler}
              label='Shipper'
              dataTest='shipper-autocomplete'
              selectShipperHandler={selectShipperHandler}
              shipper={shipper}
            />
          </span>
          <FilterMultiSelect
            className={classes.buttonRow}
            label='Equipment'
            dataTest='equipment-multiselect'
            id='equipment'
            options={equipmentOptions}
            onSelect={equipmentHandler}
            optionsLabel='Equipment'
          />
          {objectiveFourEnabled && (
            <FilterMultiSelect
              className={classes.buttonRow}
              label='Drop Type'
              dataTest='drop-type-multiselect'
              id='dropType'
              options={dropTypeOptions}
              onSelect={dropTypeHandler}
              optionsLabel='Drop Type'
            />
          )}
          <span className={classes.pricedDate}>
            <DateRangePicker
              options={dateRangeOptions}
              label='Priced Date'
              onSelect={setPricedDate}
              value={currentSelection}
            />
          </span>
        </div>
      </div>
      <TestQuoteFilter
        isTestQuote={quotesFilters.isTestQuote}
        quotesFilters={quotesFilters}
        setQuotesFilters={setQuotesFilters}
      />
      <div className={classes.sortBar}>
        <div className={classes.sortBar}>
          <span className='tab-counter'>{numberOfPriceRequests} </span> of {displayTotalCount}{' '}
          Quotes Displaying From {dateLimit}
          <AppliedQuoteFilters quotesFilters={quotesFilters} onClearFilters={clearAllHandler} />
        </div>
        <SortHistoricalQuotesFilter
          sortBy={sortBy}
          sortDirection={sortDirection}
          defaultOptionLabel={defaultOptionLabel}
          onSortingOptionSelect={onSortingOptionSelect}
          sortingOptions={sortingOptions}
        />
      </div>
      {showAutobidderMessage && (
        <InlineMessage
          className='mt4'
          message={autobidderMessage}
          type={INLINE_MESSAGE_VARIANTS.INFO}
        />
      )}
    </div>
  )
}
